import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid } from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { useLoginCheck } from "../../hooks/useLoginCheck";

const OLD_COLOR = "#6366f1"; // 旧LP: インディゴ
const NEW_COLOR = "#22d3ee"; // 新LP: シアン

const darkTheme = {
  background: "#111827",
  paper: "#1f2937",
  border: "#374151",
  text: "#f9fafb",
  subText: "#9ca3af",
  grid: "#2d3748",
  tooltip: "rgba(31, 41, 55, 0.97)",
};

const cardSx = {
  bgcolor: darkTheme.paper,
  border: `1px solid ${darkTheme.border}`,
  borderRadius: 3,
  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
};

// 期間種別の設定
const PERIODS = [
  { key: "monthly", label: "月" },
  { key: "weekly",  label: "週" },
  { key: "daily",   label: "日" },
];

// 期間ごとのラベルキーとヘッダー名
const PERIOD_CONFIG = {
  monthly: { dataKey: "month", axisLabel: "月",   title: "月別", rangeLabel: "（直近12ヶ月）", currentLabel: "（今月）" },
  weekly:  { dataKey: "week",  axisLabel: "週",   title: "週別", rangeLabel: "（直近12週）",   currentLabel: "（今週）" },
  daily:   { dataKey: "date",  axisLabel: "日付", title: "日別", rangeLabel: "（直近30日）",   currentLabel: "（今日）" },
};

// 期間切り替えボタン
const PeriodToggle = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    {PERIODS.map(({ key, label }) => (
      <Box
        key={key}
        onClick={() => onChange(key)}
        sx={{
          px: 1.5,
          py: 0.4,
          borderRadius: 1.5,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          bgcolor: value === key ? OLD_COLOR : darkTheme.border,
          color: value === key ? "#fff" : darkTheme.subText,
          transition: "all 0.15s",
          "&:hover": { opacity: 0.8 },
          userSelect: "none",
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <Box
      sx={{
        bgcolor: darkTheme.tooltip,
        border: `1px solid ${darkTheme.border}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        minWidth: 160,
      }}
    >
      {label && (
        <Typography sx={{ color: darkTheme.subText, fontSize: 12, mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {payload.map((item, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
          <Typography sx={{ color: darkTheme.text, fontSize: 13 }}>
            {item.name}:{" "}
            <strong style={{ color: item.color }}>{item.value}件</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// 円グラフ用ラベル
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight="bold">
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// 前月比バッジ
const DiffBadge = ({ curr, prev }) => {
  if (prev === 0 && curr === 0) return null;
  const diff = curr - prev;
  const pct = prev > 0 ? ((diff / prev) * 100).toFixed(1) : null;
  const up = diff >= 0;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 1,
        py: 0.3,
        borderRadius: 99,
        bgcolor: up ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
        color: up ? "#4ade80" : "#f87171",
        fontSize: 12,
        fontWeight: 600,
        mt: 0.5,
      }}
    >
      {up ? "▲" : "▼"} {Math.abs(diff)}件
      {pct !== null && <span style={{ opacity: 0.8 }}>（{up ? "+" : ""}{pct}%）</span>}
    </Box>
  );
};

function LpAnalyticsPage() {
  useLoginCheck();

  const [allData, setAllData] = useState(null);
  const [loading, setLoading] = useState(true);

  // 各セクション独立の期間状態
  const [barPeriod, setBarPeriod]   = useState("monthly");
  const [linePeriod, setLinePeriod] = useState("monthly");
  const [tablePeriod, setTablePeriod] = useState("monthly");

  // スタジオ別セクションの日付範囲フィルタ
  const [studioStartDate, setStudioStartDate] = useState("");
  const [studioEndDate, setStudioEndDate]     = useState("");
  const [studioAllPeriod, setStudioAllPeriod] = useState(true); // デフォルトは全件表示

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const params = new URLSearchParams();
        if (!studioAllPeriod) {
          if (studioStartDate) params.append("start_date", studioStartDate);
          if (studioEndDate)   params.append("end_date", studioEndDate);
        }
        const url = `${apiUrl}/api/get_lp_source_analytics/${params.toString() ? `?${params.toString()}` : ""}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("ネットワークエラー");
        const json = await res.json();
        setAllData(json);
      } catch (e) {
        console.error("データ取得エラー:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [apiUrl, studioStartDate, studioEndDate, studioAllPeriod]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: darkTheme.background, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: darkTheme.subText }}>読み込み中...</Typography>
      </Box>
    );
  }

  // 期間に応じたデータを選択
  const getChartData = (period) => {
    if (!allData) return [];
    if (period === "monthly") return allData.monthly_data ?? [];
    if (period === "weekly")  return allData.weekly_data  ?? [];
    return allData.daily_data ?? [];
  };

  const oldTotal  = allData?.old_total ?? 0;
  const newTotal  = allData?.new_total ?? 0;
  const total     = oldTotal + newTotal;
  const prevMonth = allData?.prev_month ?? { 旧LP: 0, 新LP: 0 };
  const currMonth = allData?.curr_month ?? { 旧LP: 0, 新LP: 0 };

  const pieData = [
    { name: "旧LP (newvisitor)",     value: oldTotal },
    { name: "新LP (newvisitor_new)", value: newTotal },
  ];

  const barData   = getChartData(barPeriod);
  const lineData  = getChartData(linePeriod);
  const tableData = getChartData(tablePeriod);

  return (
    <Box sx={{ bgcolor: darkTheme.background, minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: darkTheme.text }}>
          LP分析
        </Typography>
        <Typography sx={{ color: darkTheme.subText, mt: 0.5, fontSize: 14 }}>
          旧LP (newvisitor) vs 新LP (newvisitor_new) 比較
        </Typography>
      </Box>

      {/* サマリーカード */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 旧LP */}
        <Grid item xs={12} sm={4}>
          <Card sx={{ ...cardSx, borderTop: `3px solid ${OLD_COLOR}` }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: OLD_COLOR }} />
                <Typography sx={{ color: darkTheme.subText, fontSize: 13 }}>旧LP 累計</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: OLD_COLOR }}>
                {oldTotal.toLocaleString()}
                <Typography component="span" sx={{ color: darkTheme.subText, fontSize: 14, ml: 1 }}>件</Typography>
              </Typography>
              <DiffBadge curr={currMonth["旧LP"] ?? 0} prev={prevMonth["旧LP"] ?? 0} />
              <Typography sx={{ color: darkTheme.subText, fontSize: 12, mt: 0.5 }}>
                今月 {currMonth["旧LP"] ?? 0}件 / 先月 {prevMonth["旧LP"] ?? 0}件
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* 新LP */}
        <Grid item xs={12} sm={4}>
          <Card sx={{ ...cardSx, borderTop: `3px solid ${NEW_COLOR}` }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: NEW_COLOR }} />
                <Typography sx={{ color: darkTheme.subText, fontSize: 13 }}>新LP 累計</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: NEW_COLOR }}>
                {newTotal.toLocaleString()}
                <Typography component="span" sx={{ color: darkTheme.subText, fontSize: 14, ml: 1 }}>件</Typography>
              </Typography>
              <DiffBadge curr={currMonth["新LP"] ?? 0} prev={prevMonth["新LP"] ?? 0} />
              <Typography sx={{ color: darkTheme.subText, fontSize: 12, mt: 0.5 }}>
                今月 {currMonth["新LP"] ?? 0}件 / 先月 {prevMonth["新LP"] ?? 0}件
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* 合計 */}
        <Grid item xs={12} sm={4}>
          <Card sx={cardSx}>
            <CardContent sx={{ py: 2 }}>
              <Typography sx={{ color: darkTheme.subText, fontSize: 13, mb: 0.5 }}>2LP合計</Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: darkTheme.text }}>
                {total.toLocaleString()}
                <Typography component="span" sx={{ color: darkTheme.subText, fontSize: 14, ml: 1 }}>件</Typography>
              </Typography>
              {total > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", height: 8, borderRadius: 99, overflow: "hidden", bgcolor: darkTheme.border }}>
                    <Box sx={{ width: `${(oldTotal / total) * 100}%`, bgcolor: OLD_COLOR }} />
                    <Box sx={{ width: `${(newTotal / total) * 100}%`, bgcolor: NEW_COLOR }} />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                    <Typography sx={{ color: OLD_COLOR, fontSize: 11 }}>
                      旧 {((oldTotal / total) * 100).toFixed(1)}%
                    </Typography>
                    <Typography sx={{ color: NEW_COLOR, fontSize: 11 }}>
                      新 {((newTotal / total) * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 円グラフ + 棒グラフ */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* 円グラフ（累計比率は切り替え不要） */}
        <Grid item xs={12} md={4}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: darkTheme.text, mb: 1 }}>
                累計比率
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    labelLine={false}
                    label={renderPieLabel}
                  >
                    <Cell fill={OLD_COLOR} />
                    <Cell fill={NEW_COLOR} />
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    formatter={(val) => (
                      <span style={{ color: darkTheme.subText, fontSize: 12 }}>{val}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 棒グラフ（期間切り替え付き） */}
        <Grid item xs={12} md={8}>
          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: darkTheme.text }}>
                  {PERIOD_CONFIG[barPeriod].title}申込件数
                  <Typography component="span" sx={{ color: darkTheme.subText, fontSize: 12, ml: 1 }}>
                    {PERIOD_CONFIG[barPeriod].rangeLabel}
                  </Typography>
                </Typography>
                <PeriodToggle value={barPeriod} onChange={setBarPeriod} />
              </Box>
              {barData.length === 0 ? (
                <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ color: darkTheme.subText, fontSize: 13 }}>データがありません</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={barData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke={darkTheme.grid} vertical={false} />
                    <XAxis
                      dataKey={PERIOD_CONFIG[barPeriod].dataKey}
                      tick={{ fill: darkTheme.subText, fontSize: 11 }}
                      axisLine={{ stroke: darkTheme.border }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: darkTheme.subText, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                    <Legend
                      formatter={(val) => (
                        <span style={{ color: darkTheme.subText, fontSize: 12 }}>{val}</span>
                      )}
                    />
                    <Bar dataKey="旧LP" name="旧LP" fill={OLD_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="新LP" name="新LP" fill={NEW_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 折れ線グラフ（期間切り替え付き） */}
      <Card sx={{ ...cardSx, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: darkTheme.text }}>
              {PERIOD_CONFIG[linePeriod].title}トレンド比較
            </Typography>
            <PeriodToggle value={linePeriod} onChange={setLinePeriod} />
          </Box>
          {lineData.length === 0 ? (
            <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ color: darkTheme.subText, fontSize: 13 }}>データがありません</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={lineData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkTheme.grid} />
                <XAxis
                  dataKey={PERIOD_CONFIG[linePeriod].dataKey}
                  tick={{ fill: darkTheme.subText, fontSize: 11 }}
                  axisLine={{ stroke: darkTheme.border }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: darkTheme.subText, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(val) => (
                    <span style={{ color: darkTheme.subText, fontSize: 12 }}>{val}</span>
                  )}
                />
                <Line
                  type={linePeriod === "daily" ? "linear" : "monotone"}
                  dataKey="旧LP"
                  stroke={OLD_COLOR}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: OLD_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type={linePeriod === "daily" ? "linear" : "monotone"}
                  dataKey="新LP"
                  stroke={NEW_COLOR}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: NEW_COLOR, strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* 詳細テーブル（期間切り替え付き） */}
      <Card sx={cardSx}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: darkTheme.text }}>
              {PERIOD_CONFIG[tablePeriod].title}詳細
            </Typography>
            <PeriodToggle value={tablePeriod} onChange={setTablePeriod} />
          </Box>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[PERIOD_CONFIG[tablePeriod].axisLabel, "旧LP", "新LP", "合計", "旧LP比率", "新LP比率"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 16px",
                        color: darkTheme.subText,
                        fontSize: 13,
                        borderBottom: `1px solid ${darkTheme.border}`,
                        fontWeight: 600,
                        textAlign: h === PERIOD_CONFIG[tablePeriod].axisLabel ? "left" : "right",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...tableData].reverse().map((row, i) => {
                  const rowTotal = (row["旧LP"] ?? 0) + (row["新LP"] ?? 0);
                  const oldPct = rowTotal > 0 ? ((row["旧LP"] ?? 0) / rowTotal * 100).toFixed(1) : "—";
                  const newPct = rowTotal > 0 ? ((row["新LP"] ?? 0) / rowTotal * 100).toFixed(1) : "—";
                  const labelVal = row[PERIOD_CONFIG[tablePeriod].dataKey] ?? "";
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: `1px solid ${darkTheme.border}22`,
                        background: i === 0 ? `${darkTheme.border}33` : "transparent",
                      }}
                    >
                      <td style={{ padding: "11px 16px", color: i === 0 ? darkTheme.text : darkTheme.subText, fontSize: 13, fontWeight: i === 0 ? 700 : 400 }}>
                        {labelVal}
                        {i === 0 && (
                          <span style={{ color: darkTheme.subText, fontSize: 11, marginLeft: 6 }}>
                            {PERIOD_CONFIG[tablePeriod].currentLabel}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "11px 16px", color: OLD_COLOR, fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                        {(row["旧LP"] ?? 0).toLocaleString()}
                      </td>
                      <td style={{ padding: "11px 16px", color: NEW_COLOR, fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                        {(row["新LP"] ?? 0).toLocaleString()}
                      </td>
                      <td style={{ padding: "11px 16px", color: darkTheme.text, fontSize: 13, textAlign: "right" }}>
                        {rowTotal.toLocaleString()}
                      </td>
                      <td style={{ padding: "11px 16px", color: darkTheme.subText, fontSize: 13, textAlign: "right" }}>
                        {oldPct}{rowTotal > 0 && "%"}
                      </td>
                      <td style={{ padding: "11px 16px", color: darkTheme.subText, fontSize: 13, textAlign: "right" }}>
                        {newPct}{rowTotal > 0 && "%"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>

      {/* スタジオ別申込数（日付範囲フィルタ付き） */}
      <Card sx={{ ...cardSx, mt: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: darkTheme.text }}>
              スタジオ別申込数
              <Typography component="span" sx={{ color: darkTheme.subText, fontSize: 12, ml: 1 }}>
                {studioAllPeriod
                  ? "（全期間）"
                  : (studioStartDate && studioEndDate)
                    ? `（${studioStartDate} ～ ${studioEndDate}）`
                    : studioStartDate
                      ? `（${studioStartDate} 以降）`
                      : studioEndDate
                        ? `（${studioEndDate} まで）`
                        : "（全期間）"}
              </Typography>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <input
                type="date"
                value={studioStartDate}
                onChange={(e) => {
                  setStudioStartDate(e.target.value);
                  if (e.target.value) setStudioAllPeriod(false);
                }}
                style={{
                  background: darkTheme.background,
                  color: darkTheme.text,
                  border: `1px solid ${darkTheme.border}`,
                  borderRadius: 6,
                  padding: "4px 8px",
                  fontSize: 13,
                  colorScheme: "dark",
                }}
              />
              <Typography sx={{ color: darkTheme.subText, fontSize: 13 }}>～</Typography>
              <input
                type="date"
                value={studioEndDate}
                onChange={(e) => {
                  setStudioEndDate(e.target.value);
                  if (e.target.value) setStudioAllPeriod(false);
                }}
                style={{
                  background: darkTheme.background,
                  color: darkTheme.text,
                  border: `1px solid ${darkTheme.border}`,
                  borderRadius: 6,
                  padding: "4px 8px",
                  fontSize: 13,
                  colorScheme: "dark",
                }}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: darkTheme.text, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={studioAllPeriod}
                  onChange={(e) => {
                    setStudioAllPeriod(e.target.checked);
                    if (e.target.checked) {
                      setStudioStartDate("");
                      setStudioEndDate("");
                    }
                  }}
                  style={{ cursor: "pointer" }}
                />
                全件表示
              </label>
            </Box>
          </Box>

          {(() => {
            const studioData = allData?.studio_breakdown ?? [];
            if (studioData.length === 0) {
              return (
                <Box sx={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ color: darkTheme.subText, fontSize: 13 }}>
                    該当する申込はありません
                  </Typography>
                </Box>
              );
            }
            const maxTotal = Math.max(...studioData.map((s) => s["合計"] ?? 0), 1);
            const grandOld = studioData.reduce((s, r) => s + (r["旧LP"] ?? 0), 0);
            const grandNew = studioData.reduce((s, r) => s + (r["新LP"] ?? 0), 0);
            const grandTotal = grandOld + grandNew;
            return (
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["スタジオ", "旧LP", "新LP", "合計", ""].map((h, idx) => (
                        <th
                          key={idx}
                          style={{
                            padding: "10px 16px",
                            color: darkTheme.subText,
                            fontSize: 13,
                            borderBottom: `1px solid ${darkTheme.border}`,
                            fontWeight: 600,
                            textAlign: idx === 0 ? "left" : idx === 4 ? "left" : "right",
                            width: idx === 4 ? "30%" : undefined,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {studioData.map((row, i) => {
                      const oldV = row["旧LP"] ?? 0;
                      const newV = row["新LP"] ?? 0;
                      const tot  = row["合計"] ?? 0;
                      const oldW = maxTotal > 0 ? (oldV / maxTotal) * 100 : 0;
                      const newW = maxTotal > 0 ? (newV / maxTotal) * 100 : 0;
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${darkTheme.border}22` }}>
                          <td style={{ padding: "11px 16px", color: darkTheme.text, fontSize: 13, fontWeight: 500 }}>
                            {row.studio}
                          </td>
                          <td style={{ padding: "11px 16px", color: OLD_COLOR, fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                            {oldV.toLocaleString()}
                          </td>
                          <td style={{ padding: "11px 16px", color: NEW_COLOR, fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                            {newV.toLocaleString()}
                          </td>
                          <td style={{ padding: "11px 16px", color: darkTheme.text, fontSize: 13, textAlign: "right", fontWeight: 700 }}>
                            {tot.toLocaleString()}
                          </td>
                          <td style={{ padding: "11px 16px" }}>
                            <Box sx={{ display: "flex", height: 8, borderRadius: 99, overflow: "hidden", bgcolor: darkTheme.border }}>
                              <Box sx={{ width: `${oldW}%`, bgcolor: OLD_COLOR }} />
                              <Box sx={{ width: `${newW}%`, bgcolor: NEW_COLOR }} />
                            </Box>
                          </td>
                        </tr>
                      );
                    })}
                    {/* 合計行 */}
                    <tr style={{ background: `${darkTheme.border}33` }}>
                      <td style={{ padding: "11px 16px", color: darkTheme.text, fontSize: 13, fontWeight: 700 }}>
                        合計（{studioData.length}店舗）
                      </td>
                      <td style={{ padding: "11px 16px", color: OLD_COLOR, fontSize: 13, textAlign: "right", fontWeight: 700 }}>
                        {grandOld.toLocaleString()}
                      </td>
                      <td style={{ padding: "11px 16px", color: NEW_COLOR, fontSize: 13, textAlign: "right", fontWeight: 700 }}>
                        {grandNew.toLocaleString()}
                      </td>
                      <td style={{ padding: "11px 16px", color: darkTheme.text, fontSize: 13, textAlign: "right", fontWeight: 700 }}>
                        {grandTotal.toLocaleString()}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </Box>
            );
          })()}
        </CardContent>
      </Card>
    </Box>
  );
}

export default LpAnalyticsPage;
